import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import {
  useAllocateDedicatedCli,
  useAllSharedCli,
  useCliList,
  useResellerUserPicker,
  useTreeUserPicker,
} from '../api';

/**
 * Allocate CLIs onward.
 *   - SADMIN / SUB_SUPER_ADMIN: source = shared stock, users = direct children.
 *   - Reseller (ADMIN): source = own free stock, users = whole tree.
 */
export function AllocateCliToUserPage() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);

  const isSuper =
    role === USER_ROLES.S_ADMIN || role === USER_ROLES.SUB_SUPER_ADMIN;

  const allShared = useAllSharedCli(isSuper);
  const ownStock = useCliList('cli', !isSuper);
  const source = isSuper ? allShared : ownStock;

  const superUsers = useResellerUserPicker(isSuper);
  const treeUsers = useTreeUserPicker(!isSuper);
  const users = isSuper ? superUsers : treeUsers;
  const allocateDedicated = useAllocateDedicatedCli();

  const [targetUserId, setTargetUserId] = useState('');
  const [cliIds, setCliIds] = useState<string[]>([]);

  const cliOptions = useMemo(
    () =>
      (source.data ?? []).map((c) => ({
        value: String(c.cli),
        label: c.pilotNumber ? `${c.cli} (${c.pilotNumber})` : c.cli,
      })),
    [source.data],
  );

  const userOptions = useMemo(
    () =>
      (users.data ?? []).map((u) => ({
        value: String(u.userId),
        label: u.name ? `${u.username} — ${u.name}` : u.username,
      })),
    [users.data],
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetUserId) {
      toast.error('Pick a user to allocate to.');
      return;
    }
    if (cliIds.length === 0) {
      toast.error('Pick at least one CLI.');
      return;
    }
    allocateDedicated.mutate(
      {
        userId: String(targetUserId),
        allocationType: 'manual',
        cli: cliIds.join(','),
      },
      {
        onSuccess: (res) => {
          if (res?.message !== 'CLI Allocated Successfully') {
            toast.error(res?.message ?? 'Could not allocate CLIs.');
            return;
          }
          toast.success(res.message);
          navigate('/cli-list');
        },
        onError: () => toast.error('Could not allocate CLIs.'),
      },
    );
  };

  return (
    <ScrollPage>
      <PageHeader
        title="Allocate CLI"
        description="Assign one or more CLIs to a user. They appear in the user's CLI list immediately."
        actions={
          <Button asChild variant="outline">
            <Link to="/cli-list">
              <ArrowLeft className="h-4 w-4" />
              Back to CLIs
            </Link>
          </Button>
        }
      />

      <Card className="sm:max-w-2xl">
        <CardHeader>
          <CardTitle>Allocation</CardTitle>
          <CardDescription>
            {isSuper
              ? 'Pick any CLI from your shared pool.'
              : 'Only CLIs your parent allocated to you (and you have not passed on) can be granted onward.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="grid gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="targetUser">
                User<span className="ml-0.5 text-destructive">*</span>
              </Label>
              <Select
                value={targetUserId}
                onValueChange={setTargetUserId}
                disabled={users.isLoading}
              >
                <SelectTrigger id="targetUser">
                  <SelectValue
                    placeholder={
                      users.isLoading ? 'Loading users…' : 'Pick a user'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {userOptions.map((u) => (
                    <SelectItem key={u.value} value={u.value}>
                      {u.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-1.5">
              <Label>
                CLIs<span className="ml-0.5 text-destructive">*</span>
              </Label>
              <MultiSelect
                options={cliOptions}
                value={cliIds}
                onChange={setCliIds}
                placeholder={
                  source.isLoading ? 'Loading CLIs…' : 'Pick one or more CLIs'
                }
                searchPlaceholder="Filter CLIs…"
                disabled={source.isLoading}
              />
              <p className="text-xs text-muted-foreground">
                {cliIds.length} selected.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Button type="submit" disabled={allocateDedicated.isPending}>
                {allocateDedicated.isPending && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Allocate
              </Button>
              <Button asChild type="button" variant="ghost">
                <Link to="/cli-list">Cancel</Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </ScrollPage>
  );
}
